/**
 * Quadro binario da mensagem, antes do disfarce (docs/03-formato-mensagem.md).
 *
 *   0   1  versao
 *   1   1  tipo
 *   2   2  flags
 *   4   4  TTL em segundos
 *   8  80  cabecalho do ratchet, cifrado (tamanho fixo)
 *  88   n  ciphertext + tag
 *
 * O prefixo de 8 bytes entra no AD da cifra. Sem isso, um adversario poderia
 * apagar a flag "autodestruir" ou zerar o TTL sem quebrar a autenticacao.
 */
import { concat, readUint16BE, readUint32BE, writeUint16BE, writeUint32BE } from './bytes.js'
import { MalformedFrameError } from './errors.js'
import type { RatchetMessage, RatchetSession } from './ratchet.js'
import { ENCRYPTED_HEADER_BYTES } from './ratchet.js'
import { SIZES } from './sodium.js'

export const MESSAGE_FORMAT_VERSION = 1

export const MessageType = {
  Text: 0,
  Audio: 1,
  Invite: 2,
  Control: 3,
  Otp: 4,
} as const
export type MessageType = (typeof MessageType)[keyof typeof MessageType]

export const MessageFlag = {
  ListenOnce: 1 << 0,
  SelfDestruct: 1 << 1,
  /**
   * O texto claro foi comprimido ANTES de cifrar.
   *
   * O bit mora no prefixo, que entra no AD: nao da para liga-lo ou desliga-lo
   * no caminho sem quebrar a autenticacao. Sem isso, um adversario faria quem
   * recebe tentar descomprimir bytes que nao sao comprimidos.
   *
   * A COMPRESSAO E OPCIONAL DE PROPOSITO, e nao por preguica. Ela troca
   * tamanho por vazamento: o comprimento do que sai passa a depender de
   * QUANTO o texto comprime, e isso e uma pista sobre o conteudo que o texto
   * cifrado sozinho nao daria. Em texto curto ela ainda por cima cresce — 2
   * bytes viram 6. Por isso quem chama decide, mensagem a mensagem, e o
   * criterio esta em `comprimir()` no app.
   *
   * O crypto-core define o bit e nao comprime nada: compressao e API de
   * plataforma, e este pacote nao depende de nenhuma.
   */
  Compressed: 1 << 2,
} as const

export const FRAME_PREFIX_BYTES = 8
export const FRAME_OVERHEAD_BYTES =
  FRAME_PREFIX_BYTES + ENCRYPTED_HEADER_BYTES + SIZES.aeadNonce + SIZES.aeadTag

export interface FrameMeta {
  readonly type: MessageType
  readonly flags: number
  readonly ttlSeconds: number
}

export interface DecodedFrame extends FrameMeta {
  readonly version: number
  readonly prefix: Uint8Array
  readonly ratchetMessage: RatchetMessage
}

function encodePrefix(meta: FrameMeta): Uint8Array {
  if (meta.flags < 0 || meta.flags > 0xffff) throw new MalformedFrameError('flags fora de 16 bits')
  if (meta.ttlSeconds < 0 || meta.ttlSeconds > 0xffffffff) {
    throw new MalformedFrameError('TTL fora de 32 bits')
  }
  const prefix = new Uint8Array(FRAME_PREFIX_BYTES)
  prefix[0] = MESSAGE_FORMAT_VERSION
  prefix[1] = meta.type
  writeUint16BE(meta.flags, prefix, 2)
  writeUint32BE(meta.ttlSeconds, prefix, 4)
  return prefix
}

export function hasFlag(flags: number, flag: number): boolean {
  return (flags & flag) !== 0
}

/** Cifra o conteudo e monta o quadro pronto para o disfarce. */
export function sealFrame(
  session: RatchetSession,
  sessionAssociatedData: Uint8Array,
  meta: FrameMeta,
  plaintext: Uint8Array,
): Uint8Array {
  const prefix = encodePrefix(meta)
  const message = session.encrypt(plaintext, concat(sessionAssociatedData, prefix))
  return concat(prefix, message.encryptedHeader, message.ciphertext)
}

export function decodeFrame(bytes: Uint8Array): DecodedFrame {
  if (bytes.length < FRAME_PREFIX_BYTES + ENCRYPTED_HEADER_BYTES) {
    throw new MalformedFrameError('curto demais para conter cabecalho')
  }
  const version = bytes[0] ?? 0
  if (version !== MESSAGE_FORMAT_VERSION) {
    throw new MalformedFrameError(`versao ${version} desconhecida`)
  }
  const type = (bytes[1] ?? 0) as MessageType
  if (!Object.values(MessageType).includes(type)) {
    throw new MalformedFrameError(`tipo ${String(bytes[1])} desconhecido`)
  }
  return {
    version,
    type,
    flags: readUint16BE(bytes, 2),
    ttlSeconds: readUint32BE(bytes, 4),
    prefix: bytes.slice(0, FRAME_PREFIX_BYTES),
    ratchetMessage: {
      encryptedHeader: bytes.slice(FRAME_PREFIX_BYTES, FRAME_PREFIX_BYTES + ENCRYPTED_HEADER_BYTES),
      ciphertext: bytes.slice(FRAME_PREFIX_BYTES + ENCRYPTED_HEADER_BYTES),
    },
  }
}

export interface OpenedFrame extends FrameMeta {
  readonly plaintext: Uint8Array
}

export function openFrame(
  session: RatchetSession,
  sessionAssociatedData: Uint8Array,
  bytes: Uint8Array,
): OpenedFrame {
  const frame = decodeFrame(bytes)
  const plaintext = session.decrypt(
    frame.ratchetMessage,
    concat(sessionAssociatedData, frame.prefix),
  )
  return { type: frame.type, flags: frame.flags, ttlSeconds: frame.ttlSeconds, plaintext }
}
